var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { tokenize } from './tokenizer';
import { buildTree } from './structurer';
import { decodeUtfBOM } from './decoding';
/**
 * Supported Gedcom file encoding schemes.
 */
export var FileEncoding;
(function (FileEncoding) {
    FileEncoding["Utf8"] = "UTF-8";
    FileEncoding["Ansel"] = "ANSEL";
    FileEncoding["Cp1252"] = "Cp1252";
    FileEncoding["Macintosh"] = "Macintosh";
    FileEncoding["Cp850"] = "Cp850";
    FileEncoding["Utf16be"] = "UTF-16be";
    FileEncoding["Utf16le"] = "UTF-16le";
})(FileEncoding || (FileEncoding = {}));
/**
 * Extracts the file metadata (see {@link FileMetadata}). The metadata can then be used to guess the actual charset of the file.
 * To circumvent the bootstrapping problem this function restricts its reading to the first lines of the file only, and
 * decodes them as UTF-8.
 * @param buffer The content of the file
 * @param maxPeekBytes Maximum number of bytes to read
 * @param maxPeekLines Maximum number of lines to read
 */
export var getFileMetadata = function (buffer, maxPeekBytes, maxPeekLines) {
    var e_1, _a;
    if (maxPeekBytes === void 0) { maxPeekBytes = 1000; }
    if (maxPeekLines === void 0) { maxPeekLines = 100; }
    var _b = decodeUtfBOM(buffer.slice(0, maxPeekBytes)), inputHead = _b.output, bomCharset = _b.bomCharset; // Start with UTF-8 since file can contain a BOM
    var hasBOM = bomCharset !== null;
    var it = tokenize(inputHead, false); // Non-strict mode: break silently on error
    var i = 0;
    var array = [];
    try {
        for (var it_1 = __values(it), it_1_1 = it_1.next(); !it_1_1.done; it_1_1 = it_1.next()) {
            var line = it_1_1.value;
            if (i >= maxPeekLines) {
                break;
            }
            array.push(line);
            i += 1;
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (it_1_1 && !it_1_1.done && (_a = it_1.return)) _a.call(it_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var tree = buildTree(array);
    var header = tree.children.filter(function (c) { return c.tag === "HEAD" /* Header */; });
    var char = header.flatMap(function (c) { return c.children.filter(function (n) { return n.tag === "CHAR" /* Character */ || n.tag === "CHARACTER" /* CharacterAlt */; }); });
    var charOpt = char.length > 0 ? char[0].value : null;
    var source = header.flatMap(function (c) { return c.children.filter(function (n) { return n.tag === "SOUR" /* Source */; }); });
    var sourceOpt = source.length > 0 ? source[0].value : null;
    var version = source.flatMap(function (c) { return c.children.filter(function (n) { return n.tag === "VERS" /* Version */; }); });
    var versionOpt = version.length > 0 ? version[0].value : null;
    return { sourceEncoding: charOpt, sourceProvider: sourceOpt, sourceProviderVersion: versionOpt, fileHasBOM: hasBOM };
};
/**
 * Detects the file charset using a set of heuristics. Has proven to work great in practice.
 * If you encounter a file for which this procedure doesn't work as intended please do open an issue
 * at: https://github.com/arbre-app/read-gedcom/issues.
 * @param buffer The content of the file
 */
export var detectCharset = function (buffer) {
    // eslint-disable-next-line
    var _a = getFileMetadata(buffer), sourceEncoding = _a.sourceEncoding, sourceProvider = _a.sourceProvider, sourceProviderVersion = _a.sourceProviderVersion, fileHasBOM = _a.fileHasBOM;
    // Estimate an encoding without knowing the provider
    function estimateEncoding() {
        if (sourceEncoding === "UTF-8" /* Utf8 */) {
            return "UTF-8" /* Utf8 */;
        }
        else if (sourceEncoding === "ANSEL" /* Ansel */) {
            return "ANSEL" /* Ansel */;
        }
        else if (sourceEncoding === "ASCII" /* Ascii */) {
            return "Cp1252" /* Cp1252 */;
        }
        else if (sourceEncoding === "ANSI" /* Ansi */) {
            return "Cp1252" /* Cp1252 */;
        }
        else if (sourceEncoding === "UNICODE" /* Unicode */) {
            return "UTF-16be" /* Utf16be */; // RFC 2781
        }
        else if (sourceEncoding === 'WINDOWS') {
            return "Cp1252" /* Cp1252 */;
        }
        else if (sourceEncoding === 'MACINTOSH') {
            return "Macintosh" /* Macintosh */;
        }
        else if (sourceEncoding === 'IBMPC') {
            return "Cp850" /* Cp850 */;
        }
        else if (sourceEncoding === 'MSDOS') {
            return "Cp850" /* Cp850 */;
        }
        else if (sourceEncoding === 'UNIX') {
            return "Cp1252" /* Cp1252 */;
        }
        else if (sourceEncoding === 'UTF8') { // Spelling mistake
            return "UTF-8" /* Utf8 */;
        }
        else { // Unknown encoding
            return "UTF-8" /* Utf8 */; // Defaults to UTF-8
        }
    }
    if (fileHasBOM) { // Short-circuit: must be one of UTF-{8,16,32}
        return "UTF-8" /* Utf8 */;
    }
    if (sourceProvider === 'GeneWeb') { // Geneweb
        if (sourceEncoding === "ASCII" /* Ascii */) {
            return "Cp1252" /* Cp1252 */;
        }
        else if (sourceEncoding === "UTF-8" /* Utf8 */) {
            return "UTF-8" /* Utf8 */;
        }
        else {
            return estimateEncoding();
        }
    }
    else if (sourceProvider != null && sourceProvider.startsWith('HEREDIS')) { // Heredis
        if (sourceEncoding === "ANSI" /* Ansi */) {
            return "Cp1252" /* Cp1252 */;
        }
        else {
            return estimateEncoding();
        }
    }
    else if (sourceProvider === 'GENEATIQUE') { // Généatique
        if (sourceEncoding === "ANSEL" /* Ansel */) {
            return "ANSEL" /* Ansel */;
        }
        else {
            return estimateEncoding();
        }
    }
    else if (sourceProvider === 'Gramps') { // Gramps
        if (sourceEncoding === "UTF-8" /* Utf8 */) {
            return "UTF-8" /* Utf8 */;
        }
        else {
            return estimateEncoding();
        }
    }
    else { // Unknown provider
        return estimateEncoding();
    }
};
//# sourceMappingURL=decoder.js.map